/**
 * Cloud save module — get/set a per-game JSON blob.
 *
 * Direct DB access via the Supabase client; RLS policies on
 * `cloud_saves` enforce owner-only access. No Edge Function needed.
 */
import { VibecadeError, VibecadeErrorCode } from './types';
import type { SdkContext } from './internal/context';
import { wrapError } from './internal/errors';
import { withReadTimeout } from './internal/readTimeout';

export class CloudSaveModule {
  constructor(private readonly ctx: SdkContext) {}

  async get<T = unknown>(): Promise<T | null> {
    try {
      const playerId = this.requirePlayerId();
      const { data, error } = await withReadTimeout('cloud save read', (signal) =>
        this.ctx.db
          .from('cloud_saves')
          .select('data')
          .eq('player_id', playerId)
          .eq('game_id', this.ctx.config.gameId)
          .abortSignal(signal)
          .maybeSingle(),
      );
      if (error) throw new VibecadeError(VibecadeErrorCode.CloudSaveFailed, error.message);
      if (!data) return null;
      return data.data as T;
    } catch (e) {
      throw wrapError(e, VibecadeErrorCode.CloudSaveFailed, 'Could not load cloud save');
    }
  }

  async set<T>(data: T): Promise<void> {
    try {
      const playerId = this.requirePlayerId();
      const { error } = await withReadTimeout('cloud save write', (signal) =>
        this.ctx.db
          .from('cloud_saves')
          .upsert(
            {
              player_id: playerId,
              game_id: this.ctx.config.gameId,
              data: data as unknown,
            },
            { onConflict: 'player_id,game_id' },
          )
          .abortSignal(signal),
      );
      if (error) throw new VibecadeError(VibecadeErrorCode.CloudSaveFailed, error.message);
    } catch (e) {
      throw wrapError(e, VibecadeErrorCode.CloudSaveFailed, 'Could not save to cloud');
    }
  }

  private requirePlayerId(): string {
    const id = this.ctx.player.current?.id;
    if (!id) {
      throw new VibecadeError(
        VibecadeErrorCode.NotInitialized,
        'cloudSave requires a player — call init() first',
      );
    }
    return id;
  }
}
